/// BPMNPropertyForm: Renders property form based on BPMN element type.
/// Shows common fields (name, ID, type) and type-specific sections
/// for service tasks, user tasks, script tasks, timer events, gateways, etc.

import { PropertyField } from "./PropertyField";
import type { BPMNElementProperties, BPMNElementType } from "@/types/properties";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BPMNPropertyFormProps {
  properties: BPMNElementProperties;
  onChange: (updated: Partial<BPMNElementProperties>) => void;
}

// ---------------------------------------------------------------------------
// Type helpers
// ---------------------------------------------------------------------------

const SERVICE_TASK_TYPES: BPMNElementType[] = ["serviceTask"];
const USER_TASK_TYPES: BPMNElementType[] = ["userTask"];
const SCRIPT_TASK_TYPES: BPMNElementType[] = ["scriptTask"];
const TIMER_EVENT_TYPES: BPMNElementType[] = ["timerEvent"];
const FLOW_TYPES: BPMNElementType[] = ["sequenceFlow", "messageFlow"];
const MULTI_INSTANCE_CAPABLE: BPMNElementType[] = [
  "task",
  "serviceTask",
  "userTask",
  "scriptTask",
  "sendTask",
  "receiveTask",
  "subprocess",
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BPMNPropertyForm({ properties, onChange }: BPMNPropertyFormProps) {
  const { type } = properties;

  const isServiceTask = SERVICE_TASK_TYPES.includes(type);
  const isUserTask = USER_TASK_TYPES.includes(type);
  const isScriptTask = SCRIPT_TASK_TYPES.includes(type);
  const isTimerEvent = TIMER_EVENT_TYPES.includes(type);
  const isFlow = FLOW_TYPES.includes(type);
  const canMultiInstance = MULTI_INSTANCE_CAPABLE.includes(type);

  return (
    <div className="flex flex-col gap-4 p-3">
      {/* General section */}
      <Section title="General">
        <PropertyField
          label="Name"
          type="text"
          value={properties.name}
          onChange={(v) => onChange({ name: String(v) })}
          placeholder="Element name"
        />
        <PropertyField
          label="ID"
          type="text"
          value={properties.id}
          onChange={(v) => onChange({ id: String(v) })}
          placeholder="e.g. Activity_1"
        />
        <PropertyField
          label="Type"
          type="text"
          value={formatType(type)}
          onChange={() => undefined}
          disabled
        />
      </Section>

      {/* Service task section */}
      {isServiceTask && (
        <Section title="Service Task">
          <PropertyField
            label="Job Type"
            type="text"
            value={properties.jobType ?? ""}
            onChange={(v) => onChange({ jobType: String(v) })}
            placeholder="e.g. payment-processing"
          />
          <PropertyField
            label="Retries"
            type="number"
            value={properties.retries ?? 3}
            onChange={(v) => onChange({ retries: Number(v) })}
          />
          <PropertyField
            label="Custom Headers"
            type="json"
            value={properties.jobCustomHeaders ?? {}}
            onChange={(v) => onChange({ jobCustomHeaders: v as Record<string, string> })}
          />
        </Section>
      )}

      {/* User task section */}
      {isUserTask && (
        <Section title="User Task">
          <PropertyField
            label="Form Key"
            type="text"
            value={properties.formKey ?? ""}
            onChange={(v) => onChange({ formKey: String(v) })}
            placeholder="e.g. approval-form"
          />
          <PropertyField
            label="Assignee"
            type="text"
            value={properties.assignee ?? ""}
            onChange={(v) => onChange({ assignee: String(v) })}
            placeholder="e.g. john.doe"
          />
          <PropertyField
            label="Candidate Groups"
            type="text"
            value={properties.candidateGroups ?? ""}
            onChange={(v) => onChange({ candidateGroups: String(v) })}
            placeholder="e.g. managers, approvers"
          />
        </Section>
      )}

      {/* Script task section */}
      {isScriptTask && (
        <Section title="Script Task">
          <PropertyField
            label="Language"
            type="select"
            value={properties.scriptLanguage ?? ""}
            onChange={(v) => onChange({ scriptLanguage: String(v) })}
            options={[
              { value: "feel", label: "FEEL" },
              { value: "javascript", label: "JavaScript" },
              { value: "groovy", label: "Groovy" },
            ]}
          />
          <PropertyField
            label="Script Body"
            type="expression"
            value={properties.scriptBody ?? ""}
            onChange={(v) => onChange({ scriptBody: String(v) })}
            placeholder="Enter script..."
          />
        </Section>
      )}

      {/* Timer event section */}
      {isTimerEvent && (
        <Section title="Timer">
          <PropertyField
            label="Timer Type"
            type="select"
            value={properties.timerType ?? ""}
            onChange={(v) => onChange({ timerType: v as "duration" | "date" | "cycle" })}
            options={[
              { value: "duration", label: "Duration" },
              { value: "date", label: "Date" },
              { value: "cycle", label: "Cycle" },
            ]}
          />
          <PropertyField
            label="Expression"
            type="expression"
            value={properties.timerExpression ?? ""}
            onChange={(v) => onChange({ timerExpression: String(v) })}
            placeholder="e.g. PT1H, 2024-01-01T00:00:00Z, R3/PT10M"
          />
        </Section>
      )}

      {/* Sequence flow condition section */}
      {isFlow && (
        <Section title="Condition">
          <PropertyField
            label="Condition Expression"
            type="expression"
            value={properties.conditionExpression ?? ""}
            onChange={(v) => onChange({ conditionExpression: String(v) })}
            placeholder="e.g. = amount > 1000"
          />
          <PropertyField
            label="Default Flow"
            type="checkbox"
            value={properties.isDefault ?? false}
            onChange={(v) => onChange({ isDefault: Boolean(v) })}
          />
        </Section>
      )}

      {/* Multi-instance section */}
      {canMultiInstance && (
        <Section title="Multi-Instance">
          <PropertyField
            label="Multi-Instance"
            type="checkbox"
            value={properties.isMultiInstance ?? false}
            onChange={(v) => onChange({ isMultiInstance: Boolean(v) })}
          />
          {properties.isMultiInstance && (
            <>
              <PropertyField
                label="Sequential"
                type="checkbox"
                value={properties.isSequential ?? false}
                onChange={(v) => onChange({ isSequential: Boolean(v) })}
              />
              <PropertyField
                label="Collection Expression"
                type="expression"
                value={properties.collectionExpression ?? ""}
                onChange={(v) => onChange({ collectionExpression: String(v) })}
                placeholder="e.g. = items"
              />
              <PropertyField
                label="Element Variable"
                type="text"
                value={properties.elementVariable ?? ""}
                onChange={(v) => onChange({ elementVariable: String(v) })}
                placeholder="e.g. item"
              />
            </>
          )}
        </Section>
      )}

      {/* IO Mappings section */}
      <Section title="Input/Output Mappings">
        <div className="text-xs text-zinc-500">
          {(properties.inputMappings?.length ?? 0) > 0
            ? `${properties.inputMappings?.length ?? 0} input mapping(s)`
            : "No input mappings"}
        </div>
        <div className="text-xs text-zinc-500">
          {(properties.outputMappings?.length ?? 0) > 0
            ? `${properties.outputMappings?.length ?? 0} output mapping(s)`
            : "No output mappings"}
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{title}</div>
      <div className="flex flex-col gap-2 pl-1">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format camelCase type to readable label. */
function formatType(type: string): string {
  return type.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
}
